(function () {
  if (window.socialGalleryInit) return;
  window.socialGalleryInit = true;

  const vimeoPlayers = new Map();
  let selectedVimeo;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = window.matchMedia('(min-width: 768px)');
  const canPreview = () => desktop.matches && !reducedMotion.matches && !navigator.connection?.saveData;
  let sdk;
  function loadSdk() {
    if (window.Vimeo?.Player) return Promise.resolve();
    if (!sdk) sdk = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://player.vimeo.com/api/player.js';
      script.onload = resolve;
      script.onerror = () => { script.remove(); sdk = null; reject(new Error('Video unavailable')); };
      document.head.appendChild(script);
    });
    return sdk;
  }
  const vimeoObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const state = vimeoPlayers.get(entry.target.querySelector('[data-social-vimeo]'));
      if (!state) return;
      state.visible = entry.isIntersecting;
      state.update();
    });
  });
  document.addEventListener('mobile-rail:change', (event) => {
    event.target.querySelectorAll('[data-social-vimeo]').forEach((iframe) => {
      if (iframe.closest('.video-tile').inert) vimeoPlayers.get(iframe)?.stop().catch(() => {});
    });
  });
  const updatePreviews = () => vimeoPlayers.forEach((state) => state.update());
  desktop.addEventListener('change', updatePreviews);
  reducedMotion.addEventListener('change', updatePreviews);
  document.addEventListener('visibilitychange', updatePreviews);

  function initVimeo(root = document) {
    root.querySelectorAll('[data-social-vimeo]').forEach((iframe) => {
      if (vimeoPlayers.has(iframe)) return;
      let player;
      let pending;
      const tile = iframe.closest('.video-tile');
      const sound = tile.querySelector('[data-vimeo-sound]');
      const pause = tile.querySelector('[data-vimeo-pause]');
      const label = tile.querySelector('[data-vimeo-label]');
      const mutedIcon = tile.querySelector('[data-vimeo-muted-icon]');
      const unmutedIcon = tile.querySelector('[data-vimeo-unmuted-icon]');
      const playIcon = tile.querySelector('[data-vimeo-play-icon]');
      const pauseIcon = tile.querySelector('[data-vimeo-pause-icon]');
      let audible = false;
      let paused = true;
      let manualPause = false;
      let revision = 0;
      let reading = 0;
      let busy = false;
      let active = false;
      let previewing = false;
      let promoted = false;
      let operation = Promise.resolve();
      const state = { visible: false, player: null, stop, update };
      vimeoPlayers.set(iframe, state);
      selectedVimeo ??= state;
      vimeoObserver.observe(tile);

      function superseded() {
        return state !== selectedVimeo;
      }

      function wanted() {
        return state === selectedVimeo && state.visible && !document.hidden && canPreview() && !manualPause;
      }

      function select() {
        selectedVimeo = state;
        return Promise.all([...vimeoPlayers.values()].filter((other) => other !== state).map((other) => other.stop()))
          .then(() => true, () => false);
      }

      function stop() {
        revision++;
        busy = false;
        active = false;
        return player ? player.pause().catch(() => player.setMuted(true)).finally(refresh) : Promise.resolve();
      }

      function run(action) {
        const version = ++revision;
        busy = true;
        const valid = () => version === revision && !superseded() && state.visible && !tile.inert && tile.isConnected && !document.hidden;
        operation = operation.then(async () => {
          if (valid()) await action(valid);
        }).catch(() => {}).then(refresh).finally(() => {
          if (version === revision) busy = false;
        });
        return operation;
      }

      async function refresh() {
        if (!player) return;
        const read = ++reading;
        try {
          const [isPaused, muted, volume] = await Promise.all([player.getPaused(), player.getMuted(), player.getVolume()]);
          if (read !== reading || !tile.isConnected) return;
          audible = volume > 0 && !muted;
          syncPause(isPaused);
        } catch {}
      }

      async function ensurePlayer(requested = false) {
        if (pending) return pending;
        if (player) return player;
        pending = loadSdk().then(async () => {
          if (!tile.isConnected || superseded() || (!requested && !wanted())) return null;
          iframe.src = iframe.dataset.src;
          const instance = new window.Vimeo.Player(iframe);
          const onPlay = () => {
            if (superseded() || tile.inert || !active || !state.visible || document.hidden) {
              stop().catch(() => {});
              return;
            }
            iframe.hidden = false;
            refresh();
            tile.querySelector('[data-vimeo-start]').toggleAttribute('hidden', true);
            pause.hidden = false;
          };
          instance.on('play', onPlay);
          instance.on('playing', onPlay);
          instance.on('pause', refresh);
          instance.on('ended', refresh);
          instance.on('volumechange', refresh);
          await instance.ready();
          player = instance;
          state.player = instance;
          await player.setQuality('360p').catch(() => {});
          return player;
        }).finally(() => { pending = null; });
        return pending;
      }

      function update() {
        if (!state.visible || document.hidden || tile.inert || superseded() || (previewing && !canPreview())) {
          stop().catch(() => {});
        } else if (wanted() && paused && !busy) {
          run(async (valid) => {
            const ready = await ensurePlayer();
            if (!ready || !valid() || !wanted()) return;
            previewing = true;
            await ready.setMuted(true);
            if (!valid() || !wanted()) return;
            active = true;
            await ready.play();
          });
        }
      }

      function syncPause(value) {
        paused = value;
        pause.setAttribute('aria-label', `${paused ? 'Play' : 'Pause'} ${iframe.title}`);
        playIcon.toggleAttribute('hidden', !paused);
        pauseIcon.toggleAttribute('hidden', paused);
        syncSound();
      }

      function syncSound() {
        const playingWithSound = audible && !paused;
        sound.setAttribute('aria-label', `${playingWithSound ? 'Mute' : 'Play'} ${iframe.title}${playingWithSound ? '' : ' with sound'}`);
        label.textContent = !player ? 'Play video' : playingWithSound ? 'Sound off' : 'Sound on';
        mutedIcon.toggleAttribute('hidden', !paused && audible);
        unmutedIcon.toggleAttribute('hidden', paused || !audible);
      }

      syncPause(paused);

      sound.addEventListener('click', () => {
        const stopped = select();
        const restart = previewing && !promoted;
        manualPause = false;
        previewing = false;
        return run(async (valid) => {
          if (!await stopped || !valid()) return;
          const ready = await ensurePlayer(true);
          if (!ready || !valid()) return;
          const [isPaused, muted, volume] = await Promise.all([ready.getPaused(), ready.getMuted(), ready.getVolume()]);
          if (!valid()) return;
          if (!isPaused && !muted && volume > 0) {
            await ready.setMuted(true);
            return;
          }
          await ready.setQuality('auto').catch(() => {});
          if (!valid()) return;
          if (restart) await ready.setCurrentTime(0);
          if (!valid()) return;
          await ready.setMuted(false);
          if (!valid()) return;
          await ready.setVolume(1);
          if (!valid()) return;
          active = true;
          promoted = true;
          if (isPaused) await ready.play();
        });
      });

      pause.addEventListener('click', () => {
        if (!player) return;
        if (!paused) {
          manualPause = true;
          return stop().catch(() => {});
        }
        const stopped = select();
        manualPause = false;
        previewing = false;
        return run(async (valid) => {
          if (!await stopped || !valid()) return;
          active = true;
          await player.play();
        });
      });
    });
  }

  initVimeo();
  document.addEventListener('shopify:section:load', (event) => initVimeo(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-social-vimeo]').forEach((iframe) => {
      vimeoObserver.unobserve(iframe.closest('.video-tile'));
      vimeoPlayers.get(iframe)?.stop().catch(() => {});
      vimeoPlayers.get(iframe)?.player?.destroy().catch(() => {});
      if (vimeoPlayers.get(iframe) === selectedVimeo) selectedVimeo = null;
      vimeoPlayers.delete(iframe);
    });
    selectedVimeo ??= vimeoPlayers.values().next().value;
  });

  let current = null;
  let lastHovered = null;

  function syncIcons(video) {
    const tile = video.closest('.video-tile');
    if (!tile) return;
    tile.querySelector('.play-icon')?.classList.toggle('opacity-0', !video.paused);
    tile.querySelector('.pause-icon')?.classList.toggle('opacity-0', video.paused);
    tile.querySelector('.muted-icon')?.classList.toggle('opacity-0', !video.muted);
    tile.querySelector('.unmuted-icon')?.classList.toggle('opacity-0', video.muted);
    tile.querySelector('.video-play-btn')?.setAttribute('aria-label', video.paused ? 'Play video' : 'Pause video');
    tile.querySelector('.video-audio-btn')?.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
  }

  function getVideo(tile) {
    const placeholder = tile.querySelector('[data-social-video]');
    if (!placeholder) return null;

    const existing = placeholder.querySelector('video');
    if (existing) return existing;

    let sources = [];
    try {
      sources = JSON.parse(placeholder.dataset.sources || '[]');
    } catch (e) {
      return null;
    }
    if (!sources.length) return null;

    const video = document.createElement('video');
    video.className = 'w-full h-full object-cover video-element';
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.preload = 'metadata';
    if (placeholder.dataset.poster) video.poster = placeholder.dataset.poster;

    sources.forEach((s) => {
      const source = document.createElement('source');
      source.src = s.url;
      source.type = s.mime || 'video/mp4';
      video.appendChild(source);
    });

    if (placeholder.dataset.captionsUrl) {
      video.crossOrigin = 'anonymous';
      const track = document.createElement('track');
      track.kind = 'captions';
      track.src = placeholder.dataset.captionsUrl;
      track.srclang = placeholder.dataset.captionsLanguage || 'en';
      track.label = track.srclang;
      track.default = true;
      video.appendChild(track);
      track.track.mode = 'showing';
    }

    ['play', 'pause', 'volumechange'].forEach((name) => {
      video.addEventListener(name, () => syncIcons(video));
    });

    const poster = placeholder.querySelector('img');
    if (poster) poster.replaceWith(video);
    else placeholder.appendChild(video);

    return video;
  }

  function play(tile, unmute) {
    const video = getVideo(tile);
    if (!video) return;

    if (current && current !== video) {
      current.pause();
      current.currentTime = 0;
      current.muted = true;
      syncIcons(current);
    }

    if (unmute) video.muted = false;
    video
      .play()
      .then(() => {
        if (tile.inert) video.pause();
        current = video;
        syncIcons(video);
      })
      .catch(() => syncIcons(video));
  }

  document.addEventListener('mouseover', (e) => {
    const tile = e.target.closest?.('.social-gallery .video-tile');
    if (tile === lastHovered) return;
    lastHovered = tile;
    if (tile && canPreview()) play(tile, false);
  });

  document.addEventListener('click', (e) => {
    const tile = e.target.closest?.('.social-gallery .video-tile');
    if (!tile) return;

    const playBtn = e.target.closest('.video-play-btn');
    const audioBtn = e.target.closest('.video-audio-btn');
    const video = getVideo(tile);
    if (!video) return;

    if (audioBtn) {
      video.muted = !video.muted;
      if (video.paused) play(tile, false);
      else syncIcons(video);
      return;
    }

    if (playBtn) {
      if (video.paused) play(tile, false);
      else video.pause();
      return;
    }

    if (video.paused) play(tile, true);
    else video.pause();
  });
})();
